/**
 * IMPORTANT: This file depends on sdg-australian-timezone-utils.js being loaded first
 * The utility functions are provided by window.AustralianTimezoneUtils
 * Source: src/js/build/australian-timezone-utils.js
 */


class CountdownManager {
  constructor() {
    this.elements = [];
    this.intervalId = null;
  }

  // Convert datetime string to UTC timestamp
  // Expected format: "MM/DD/YYYY HH:mm:ss AM/PM AEDT" or "MM/DD/YYYY HH:mm:ss AM/PM AEST"
  parseDateTime(datetimeStr) {
    const match = datetimeStr.match(
      /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2}):(\d{2})\s*(AM|PM)?\s*(AEDT|AEST)?$/i
    );

    if (!match) return NaN;

    const [, monthStr, dayStr, yearStr, hourStr, minStr, secStr, ampm] = match;
    const month = parseInt(monthStr, 10);
    const day = parseInt(dayStr, 10);
    const year = parseInt(yearStr, 10);
    let hours = parseInt(hourStr, 10);
    const minutes = parseInt(minStr, 10);
    const seconds = parseInt(secStr, 10);

    // 12-hour → 24-hour conversion
    if (ampm) {
      const isPM = ampm.toUpperCase() === 'PM';
      if (isPM && hours !== 12) hours += 12;    // 1-11 PM → 13-23
      if (!isPM && hours === 12) hours = 0;     // 12 AM → 0 (midnight)
    }

    // Get AU timezone offset from external utility (+11:00 for AEDT, +10:00 for AEST)
    if (!window.AustralianTimezoneUtils) {
      console.warn('AustralianTimezoneUtils not loaded. Falling back to AEST (+10).');
      return Date.UTC(year, month - 1, day, hours - 10, minutes, seconds);
    }
    const utcOffsetStr = window.AustralianTimezoneUtils.parseAustralianTimezone(datetimeStr);
    const offset = parseInt(utcOffsetStr.replace('+', '').split(':')[0], 10);

    // Date.UTC handles negative hours correctly (rolls back the day)
    return Date.UTC(year, month - 1, day, hours - offset, minutes, seconds);
  }

  // Format countdown as DD:HH:mm:ss
  formatCountdown(milliseconds) {
    if (milliseconds <= 0) return '00 : 00 : 00 : 00';

    const totalSeconds = Math.floor(milliseconds / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return `${String(days).padStart(2, '0')} : ${String(hours).padStart(2, '0')} : ${String(minutes).padStart(2, '0')} : ${String(seconds).padStart(2, '0')}`;
  }

  // Initialize all countdown elements
  init() {
    // Query all elements with countdown data
    const countdownElements = document.querySelectorAll('[data-countdown-datetime]');

    // Prepare elements array with parsed data
    this.elements = Array.from(countdownElements).map(el => {
      const datetimeStr = el.getAttribute('data-countdown-datetime');
      const targetTime = this.parseDateTime(datetimeStr);

      // Remove 'hidden' class to show the element
      el.classList.remove('hidden');

      return {
        element: el,
        targetTime: targetTime
      };
    });

    // Initial update
    this.update();

    // Start interval (update every second)
    this.intervalId = setInterval(() => this.update(), 1000);
  }

  // Update all countdowns
  update() {
    const now = Date.now();

    // Filter out expired elements and update only active ones
    this.elements = this.elements.filter(({ element, targetTime }) => {
      const remaining = targetTime - now;

      if (remaining <= 0) {
        // Set final state and mark as expired
        element.textContent = this.formatCountdown(0);
        element.classList.add('expired');
        const sectionId = element.getAttribute('data-section-id');
        if (sectionId) {
          const saleDateLabel = document.querySelector(`.section-${sectionId} .pd-a-spot-regular__sale-date-label`);
          if (saleDateLabel) {
            saleDateLabel.classList.add('hidden');
          }
          const saleIsLiveLabel = document.querySelector(`.section-${sectionId} .pd-a-spot-regular__sale-is-live-label`);
          if (saleIsLiveLabel) {
            saleIsLiveLabel.classList.remove('hidden');
          }
        }
        return false; // Remove from active elements
      }

      // Update element text
      element.textContent = this.formatCountdown(remaining);
      return true; // Keep in active elements
    });

    // Stop interval if no active countdowns remain
    if (this.elements.length === 0) {
      this.destroy();
    }
  }

  // Cleanup method
  destroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.countdownManager = new CountdownManager();
    window.countdownManager.init();
  });
} else {
  window.countdownManager = new CountdownManager();
  window.countdownManager.init();
}

// Cleanup on page unload
window.addEventListener('beforeunload', () => {
  if (window.countdownManager) {
    window.countdownManager.destroy();
  }
});
